import { useQuery } from "@apollo/client";
import { MouseEvent, useCallback, useState } from "react";
import { useIntl } from "react-intl";

import ButtonLink from "@components/v1/typography/ButtonLink";
import IconBase from "@components/v2/icons/IconBase";
import useSubscribeToJourney from "@hooks/subscriptions/useSubscribeToJourney";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import useMyExpert from "@hooks/useMyExpert";
import {
  GetPinsDocument,
  GetPinsQuery,
  GetPinsQueryVariables,
  GoalFragment as Goal,
  PinResourceTypeEnum,
  PinToggleDocument,
  PinToggleMutation,
  PinToggleMutationVariables,
  ToolEnum
} from "@typing/Generated";
import { Colors } from "@utils/colorUtils";

type Props = {
  color?: Extract<Colors, "gray-300" | "gray-200" | "gray-150" | "white" | "primary">;
  goal?: Pick<Goal, "hasDuplicateGoalInEditMode" | "id"> | null;
  journeyId: string;
  resourceId?: string;
  resourceType?: PinResourceTypeEnum;
  tool?: ToolEnum | null;
};

const PinToggle = ({ color = "white", goal, journeyId, resourceId, resourceType, tool }: Props) => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const myClient = useMyClient();
  const myExpert = useMyExpert();

  const getPinsQuery = useQuery<GetPinsQuery, GetPinsQueryVariables>(GetPinsDocument, {
    variables: {
      journeyId
    }
  });

  useSubscribeToJourney(journeyId, () => {
    getPinsQuery.refetch();
  });

  const [pinToggle] = useMutationWithValidation<PinToggleMutation, PinToggleMutationVariables>(
    PinToggleDocument,
    "pins",
    {
      update: (store, { data }) => {
        // There are only two distinct compositions of variables currently in use for retrieving
        // pins. We enumerate them here manually, but doing so is of course brittle because the
        // queries in use could change, which would require this code to change. A more robust
        // solution would be to generate all possible compositions of variables and update the cache
        // for each of them, but that seems like overkill for something that isn't all that likely
        // to change very often.
        const variableCompositions = [
          { journeyId },
          {
            hideEditModePins: true,
            journeyId,
            resourceTypes: [
              PinResourceTypeEnum.RESOURCE,
              PinResourceTypeEnum.WORKSHEET,
              PinResourceTypeEnum.ARTICLE,
              PinResourceTypeEnum.EMPLOYERBENEFIT
            ]
          }
        ];

        variableCompositions.forEach(variables => {
          const pinsCacheData = store.readQuery<GetPinsQuery, GetPinsQueryVariables>({
            query: GetPinsDocument,
            variables
          });

          if (pinsCacheData) {
            if (data?.pinToggle?.pin) {
              // In this path, we are adding the pin to the queries in cache. This could be
              // improved/more technically correct by only adding the pin to caches where
              // `hideEditModePins: true` if the pin is attached to something that is not in edit
              // mode. However, this would make the code considerably more complex without solving a
              // real world problem (end users can't see things in edit mode to pin anyway), so I
              // find it hard to justify trying to solve that problem.
              const newCacheData = {
                ...pinsCacheData,
                pins: [...pinsCacheData.pins, data.pinToggle.pin]
              };

              store.writeQuery<GetPinsQuery, GetPinsQueryVariables>({
                data: newCacheData,
                query: GetPinsDocument,
                variables
              });
            } else {
              const newPins = [
                ...pinsCacheData.pins.filter(
                  pin => (tool && pin.tool !== tool) || (resourceId && pin.resource?.id !== resourceId)
                )
              ];

              const newCacheData = {
                ...pinsCacheData,
                pins: newPins
              };

              store.writeQuery<GetPinsQuery, GetPinsQueryVariables>({
                data: newCacheData,
                query: GetPinsDocument,
                variables
              });
            }
          }
        });
      }
    }
  );

  const pinned =
    getPinsQuery.data?.pins.some(
      pin => (resourceId && pin.resource?.id === resourceId) || (tool && pin.tool === tool)
    ) ?? false;

  const handleSaveToggle = useCallback(
    (event: MouseEvent<HTMLButtonElement>, pinned: boolean) => {
      setLoading(true);
      event.preventDefault();
      event.stopPropagation();
      pinToggle({
        goalId: goal?.id,
        journeyId,
        pinned,
        resourceId,
        resourceType,
        tool
      }).then(() => {
        setLoading(false);
      });
    },
    [goal?.id, journeyId, pinToggle, resourceId, resourceType, tool]
  );

  if ((!myClient && !myExpert) || getPinsQuery.loading || goal?.hasDuplicateGoalInEditMode) {
    return null;
  }

  return (
    <ButtonLink
      aria-label={intl.formatMessage({ id: "accessibility.ariaLabels.pin" })}
      data-test={pinned ? "pinned" : "not-pinned"}
      disabled={loading}
      expand
      onClick={event => {
        handleSaveToggle(event, !pinned);
      }}
    >
      <IconBase className="block" color={pinned ? "primary" : color} icon="thumbtack" />
    </ButtonLink>
  );
};

export default PinToggle;
